import React, { useState } from "react";

import { useRouter } from "next/router";
import toast from "react-hot-toast";
import { RiQuestionFill } from "react-icons/ri";
import Select from "react-select";
import styled from "styled-components";

import { VaspDueDiligenceReviewStatus } from "../../types";
import { formatDate } from "../VASPDueDiligenceReviewSummary/VASPDueDiligenceReviewSummary";
import VaspReviewRadioButtons from "./components/VaspReviewRadioButtons";

import { Button } from "~/components/Button";
import Spacer, { Axis } from "~/components/Spacer";
import ToolTip from "~/components/Tooltip";
import { ViewerPermissions } from "~/contexts/ViewerContext";
import { postTfVaspDueDiligengeReview } from "~/endpoints/trustFramework";
import useCurrentViewerPermissions from "~/hooks/useCurrentViewerPermissions";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";
import { INCLUDE_IN_WIDGET_OPTIONS } from "~/templates/network/components/VaspsNetwork/util";
import { removeQueryParam } from "~/utils/url";

const Container = styled.section`
  width: 460px;
  height: 100%;
  max-height: 1000px;
  padding: 10px 15px 0 0;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.div`
  font-family: "Montserrat";
  font-style: normal;
  font-weight: 700;
  font-size: 22px;
  line-height: 27px;
  text-align: center;

  color: #000000;
`;

const Description = styled.div`
  width: 375px;

  font-family: "Montserrat";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-align: center;

  color: #000000;
`;

const SaveInfoButton = styled(Button)`
  min-height: 34px;
  font-style: normal;
  font-weight: 800;
  font-size: 14px;
  line-height: 120%;
  text-align: center;
  text-transform: uppercase;
  color: #ffffff;
`;

const DropdownLabel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 3px;
  width: 100%;
  padding-left: 16px;
  padding-right: 16px;
  font-size: 13px;
  font-weight: 600;
`;

const IncludeInWidget = styled(Select)`
  width: 415px;
  font-size: 14px;

  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
  }
`;

const Notes = styled.textarea`
  resize: none;
  outline: none;
  width: 415px;
  height: 100px;
  border-radius: 4px;
  border: 1px solid #e9e9e9;
  padding: 12px;

  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;

  color: #000000;

  &:hover {
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
  }
`;

const LastReviewedContainer = styled.div`
  margin-top: 8px;
  width: 415px;
  display: flex;
  justify-content: space-between;
`;

const LastReviewedDescription = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #070707;
`;

const LastReviewedValue = styled(LastReviewedDescription)`
  font-weight: 700;
`;

type VaspDueDiligencePanelProps = {
  reviewedVaspDID: string;
  vaspReviewStatus: VaspDueDiligenceReviewStatus | null;
  vaspReviewNote?: string | null;
  vaspReviewIncludeInWidget?: string | null;
  lastReviewedBy?: string | null;
  lastReviewedDate?: Date | null;
  onVaspDataUpdated: () => void;
};

export default function VaspDueDiligenceReviewPanel({
  reviewedVaspDID,
  vaspReviewStatus,
  vaspReviewNote,
  vaspReviewIncludeInWidget,
  lastReviewedBy,
  lastReviewedDate,
  onVaspDataUpdated,
}: VaspDueDiligencePanelProps) {
  const router = useRouter();
  const currentVaspDID = useCurrentViewerVaspDID();
  const viewerPermissions = useCurrentViewerPermissions();
  const [selectedRadioValue, setSelectedRadioValue] = useState<VaspDueDiligenceReviewStatus>(
    vaspReviewStatus || VaspDueDiligenceReviewStatus.MANUAL,
  );
  const [includeInWidget, setIncludeInWidget] = useState(vaspReviewIncludeInWidget);
  const [optionalNoteText, setOptionalNoteText] = useState<string>(vaspReviewNote || "");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const hasPermissionToReviewVasp = viewerPermissions.includes(ViewerPermissions["rules.set"]);

  const closeDueDiligenceSidePanel = () => {
    removeQueryParam("open_vasp_review", router);
    router.replace({ pathname: location.pathname });
    onVaspDataUpdated();
  };

  const saveReview = async () => {
    if (!hasPermissionToReviewVasp) {
      toast.error(`You don't have permission to perform VASP review`);
      return;
    }
    setIsLoading(true);

    try {
      await postTfVaspDueDiligengeReview(currentVaspDID, {
        reviewedVaspDID,
        reviewValue: selectedRadioValue,
        ...(includeInWidget && { includeInWidget }),
        ...(optionalNoteText && { note: optionalNoteText }),
      });
    } catch (err: any) {
      toast.error(`Oops an unexpected error occurred: ${err} `);
    } finally {
      setIsLoading(false);
      closeDueDiligenceSidePanel();
    }
  };

  return (
    <Container>
      <Spacer axis={Axis.VERTICAL} size={12} />

      <Title>Due Diligence Assessment</Title>
      <Spacer axis={Axis.VERTICAL} size={4} />
      <Description>
        Select your level of trust and how you would like to deal with transfers from this VASP.
      </Description>

      <Spacer axis={Axis.VERTICAL} size={16} />

      <VaspReviewRadioButtons vaspReviewStatus={selectedRadioValue} onValueSelected={setSelectedRadioValue} />

      <Spacer axis={Axis.VERTICAL} size={16} />
      <DropdownLabel>
        Approve VASP for withdrawals:
        <ToolTip
          id={"tooltip-review-vasp-withdrawal-approval-description"}
          HoverComponent={RiQuestionFill}
          hoverComponentProps={{ size: "18px" }}
        >
          <p>
            Note: <br />
            &quot;Approved for Withdrawal&quot; is managed by you to identify VASPs authorized for withdrawals.
          </p>
        </ToolTip>
      </DropdownLabel>
      <IncludeInWidget
        isSearchable={false}
        isClearable={true}
        styles={{
          control: (base: any) => ({
            ...base,
            boxShadow: "none",
            outline: "none",
          }),
        }}
        placeholder="SELECT VALUE"
        instanceId={"select"}
        onChange={(selectedOption: any) => setIncludeInWidget(selectedOption?.value)}
        options={INCLUDE_IN_WIDGET_OPTIONS}
        value={INCLUDE_IN_WIDGET_OPTIONS.find((o) => o.value === includeInWidget)}
      />

      <Spacer axis={Axis.VERTICAL} size={16} />
      <Notes
        placeholder="Add notes:"
        value={optionalNoteText}
        onChange={(event) => {
          setOptionalNoteText(event.target.value);
        }}
      />

      <Spacer axis={Axis.VERTICAL} size={16} />
      <LastReviewedContainer>
        <LastReviewedDescription>Last reviewed by</LastReviewedDescription>
        <LastReviewedValue>{lastReviewedBy ? lastReviewedBy : "----"}</LastReviewedValue>
      </LastReviewedContainer>
      <LastReviewedContainer>
        <LastReviewedDescription>Last review date</LastReviewedDescription>
        <LastReviewedValue>{lastReviewedDate ? formatDate(lastReviewedDate) : "----"}</LastReviewedValue>
      </LastReviewedContainer>

      <Spacer axis={Axis.VERTICAL} size={35} />
      <SaveInfoButton
        loading={isLoading}
        as="a"
        kind="primary"
        onClick={saveReview}
        disabled={selectedRadioValue === null}
      >
        {vaspReviewStatus === null ? "Mark Due diligence as complete" : "Update Review"}
      </SaveInfoButton>
    </Container>
  );
}
